import { version } from "@convex-dev/common";
import { LocalSyncState } from "./local_state.js";
import { MutationManager } from "./mutation_manager.js";
import { OptimisticQueryResults, } from "./optimistic_query_set.js";
import { RemoteQuerySet } from "./remote_query_set.js";
import { WebSocketManager } from "./web_socket_manager.js";
/**
 * Low-level client for directly integrating state management libraries
 * with Convex. Most developers should use higher level clients, like
 * the `HTTPConvexClient` or the React hook based `ReactClient`.
 *
 * @public
 */
export class InternalConvexClient {
    constructor(address, onTransition) {
        // Substitute http(s) with ws(s)
        const i = address.search("://");
        if (i == -1) {
            throw new Error("Provided address was not an absolute URL.");
        }
        const origin = address.substring(i + 3); // move past the double slash
        const protocol = address.substring(0, i);
        let wsProtocol;
        if (protocol === "http") {
            wsProtocol = "ws";
        }
        else if (protocol === "https") {
            wsProtocol = "wss";
        }
        else {
            throw new Error(`Unknown parent protocol ${protocol}`);
        }
        const wsUri = `${wsProtocol}://${origin}/api/${version}/sync`;
        this.remoteQuerySet = new RemoteQuerySet();
        this.state = new LocalSyncState();
        this.mutationManager = new MutationManager();
        this.optimisticQueryResults = new OptimisticQueryResults();
        this.onTransition = onTransition;
        this.nextMutationId = 0;
        this.webSocketManager = new WebSocketManager(wsUri, () => {
            // We have a new WebSocket!
            // Throw out our remote query, reissue queries
            // and outstanding mutations, and reauthenticate.
            this.remoteQuerySet = new RemoteQuerySet();
            const [querySetModification, authModification] = this.state.restart();
            if (authModification) {
                this.webSocketManager.sendMessage(authModification);
            }
            this.webSocketManager.sendMessage(querySetModification);
            for (const message of this.mutationManager.restart()) {
                this.webSocketManager.sendMessage(message);
            }
        }, (serverMessage) => {
            if (serverMessage.type == "Transition") {
                const remoteQueryResults = this.remoteQuerySet.transition(serverMessage);
                const queryTokenToValue = new Map();
                for (const [queryId, value] of remoteQueryResults) {
                    const query = {
                        value,
                        udfPath: this.state.queryPath(queryId),
                        args: this.state.queryArgs(queryId),
                    };
                    queryTokenToValue.set(this.state.queryToken(queryId), query);
                }
                const completedMutations = this.mutationManager.removeCompletedMutations(this.remoteQuerySet.timestamp());
                onTransition(this.optimisticQueryResults.ingestQueryResultsFromServer(queryTokenToValue, completedMutations));
            }
            else if (serverMessage.type == "MutationResponse") {
                this.mutationManager.onResponse(serverMessage);
            }
            else if (serverMessage.type === "QueriesFailed") {
                for (const failure of serverMessage.failures) {
                    const queryPath = this.state.queryPath(failure.queryId);
                    if (queryPath) {
                        for (const line of failure.logLines) {
                            console.log(`%c[CONVEX Q(${queryPath})] ${line}`, "color:blue");
                        }
                        console.error(`[CONVEX Q(${queryPath})] ${failure.message}`);
                    }
                }
            }
        });
    }
    setAuth(value) {
        const message = this.state.setAuth(value);
        this.webSocketManager.sendMessage(message);
    }
    setAdminAuth(value) {
        const message = this.state.setAdminAuth(value);
        this.webSocketManager.sendMessage(message);
    }
    clearAuth() {
        const message = this.state.clearAuth();
        this.webSocketManager.sendMessage(message);
    }
    subscribe(udfPath, args) {
        const { modification, queryToken, unsubscribe } = this.state.subscribe(udfPath, args);
        if (modification !== null) {
            this.webSocketManager.sendMessage(modification);
        }
        // TODO: Use FinalizationRegistry?
        return {
            queryToken,
            unsubscribe: () => {
                const modification = unsubscribe();
                if (modification) {
                    this.webSocketManager.sendMessage(modification);
                }
            },
        };
    }
    async mutate(udfPath, args, optimisticUpdate = null) {
        const mutationId = this.nextMutationId;
        this.nextMutationId++;
        if (optimisticUpdate !== null) {
            const changedQueries = this.optimisticQueryResults.applyOptimisticUpdate(optimisticUpdate, mutationId);
            this.onTransition(changedQueries);
        }
        // Wait for the WebSocket connection to be ready before firing off our request.
        // This won't be necessary once mutations are idempotent CX-657
        await this.webSocketManager.waitForConnection();
        const { message, result } = this.mutationManager.request(udfPath, args, mutationId);
        this.webSocketManager.sendMessage(message);
        return result;
    }
    async close() {
        return this.webSocketManager.close();
    }
}
//# sourceMappingURL=client.js.map